// 카엘의 2단 명암은 월드 광원·그림자를 그대로 받고, 경계만 부드럽게 연결한다.
import * as THREE from 'three/webgpu';
import { Fn, float, materialEmissive, normalView, positionViewDirection } from 'three/tsl';
import type { RimControl } from './materials';

// 본체 헐은 KaelModel이 메시 월드 배율로 나누므로 이 값이 최종 월드 두께다.
export const KAEL_OUTLINE_M = 0.009;
// 검은 손 본 배율을 소켓에서 상쇄한 뒤 모델 배율 1.5를 받는다: 0.006 × 1.5 = 본체와 같은 0.009m.
export const KAEL_SWORD_OUTLINE_M = 0.006;

export function createKaelSurface(src: THREE.MeshStandardMaterial, rim: RimControl): THREE.MeshToonNodeMaterial {
  // 기본 툰은 빛 반대쪽에도 직사광 70%를 주고 경계를 한 픽셀로 자른다.
  // 두 색면은 유지하되 그늘의 직사광을 줄여 반구광의 지역색을 살리고 갑옷·붉은 천의 암부 분리를 남긴다.
  const ramp = new Uint8Array(64 * 4);
  for (let i = 0; i < 64; i++) {
    const t = THREE.MathUtils.smoothstep(i / 63, 0.46, 0.72);
    const light = Math.round((0.50 + 0.44 * t) * 255);
    ramp.set([light, light, light, 255], i * 4);
  }
  const gradient = new THREE.DataTexture(ramp, 64, 1);
  gradient.minFilter = gradient.magFilter = THREE.LinearFilter;
  gradient.generateMipmaps = false;
  gradient.needsUpdate = true;
  const m = new THREE.MeshToonNodeMaterial({
    color: src.color, map: src.map, normalMap: src.normalMap, side: src.side,
    gradientMap: gradient, fog: true,
  });
  // 역광에서 자기 그림자가 앞면 전체를 검게 만들지 않게 한다. 월드 그림자의 방향·차이는 유지한다.
  // three 런타임은 shadow 노드를 인자로 넘긴다. @types의 인자 없는 서명만 보정한다.
  m.receivedShadowNode = Fn(([shadow]: [THREE.Node<'vec3'>]) => shadow.mul(0.65).add(0.35)) as unknown as () => THREE.Node;
  m.normalScale.copy(src.normalScale);
  m.emissive = new THREE.Color(0x000000);
  const fres = float(1).sub(normalView.dot(positionViewDirection).clamp(0, 1)).pow(4);
  // 상시 림만 낮춘다. 각성에서 더하는 림과 피격 emissive는 유지한다.
  const strength = rim.strength.min(0.55).mul(0.22).add(rim.strength.sub(0.55).max(0));
  (m as unknown as { emissiveNode: unknown }).emissiveNode = materialEmissive.add(rim.color.mul(fres).mul(strength));
  m.addEventListener('dispose', () => gradient.dispose());
  return m;
}
